/**
 * Voertuig-onderhoudsrapport (FO-327, batch-37) — gedeeld tussen API, backoffice
 * en portal.
 *
 * De leverancier mailt dagelijks een "Vehicle maintenance report"-PDF met per
 * rij VIN (chassisnummer), odometer, dagen tot onderhoud ("-" = onbekend) en
 * km tot onderhoud (negatief = achterstallig, 0 = onderhoud nu). De API leest
 * die rapporten in (herkenningsprofiel + parser + VIN-matching) en leidt per
 * voertuig de actuele onderhoud-verwachting af (nieuwste rapport wint).
 *
 * Dit bestand bevat:
 *   - het Zod-rijschema (deterministische parser én AI-fallback valideren hiermee)
 *   - de toestand-classifier + weergave-teksten van de verwachting-badge
 *     (gepland / binnenkort / achterstallig / onderhoud nu)
 *   - de wagenpark-instellingen (drempels + herkenningsprofiel) request-schema
 *   - de koppel-actie (werkvoorraad -> voertuig) request-schema
 *   - de DTO's voor ingest-log, rapportregels en verwachtingen
 *
 * Gate-keys: beheer (ingest-log, werkvoorraad, drempels) = `VOERTUIGEN`;
 * portal (eigen voertuig-badge) = `VOERTUIGEN` canView + portal-access.
 */

import { z } from 'zod'

// ─── Rijschema (parser-output) ───────────────────────────────────────────────

/** VIN: 17 alfanumerieke tekens (uppercase; parser normaliseert vooraf). */
export const VIN_REGEX = /^[A-Z0-9]{17}$/

/**
 * Eén rapportregel zoals de parser (deterministisch of AI-fallback) hem
 * oplevert. De parser normaliseert vóór validatie: VIN uppercase zonder
 * spaties, "-" in de dagen-kolom -> null, getallen als number.
 */
export const onderhoudsrapportRijSchema = z.object({
  vin: z.string().regex(VIN_REGEX, 'VIN moet 17 alfanumerieke tekens zijn'),
  odometer: z.number().int().min(0).max(2_000_000),
  /** Dagen tot onderhoud; null = "-" in het rapport (alleen km-grens bekend). */
  dagenTotOnderhoud: z.number().int().min(-10_000).max(10_000).nullable(),
  /** Km tot onderhoud; negatief = achterstallig met dat aantal km, 0 = nu. */
  kmTotOnderhoud: z.number().int().min(-1_000_000).max(1_000_000),
  omschrijving: z.string().max(500).nullable(),
})
export type OnderhoudsrapportRij = z.infer<typeof onderhoudsrapportRijSchema>

// ─── Verwachting-toestand (badge-classificatie) ──────────────────────────────

export const ONDERHOUD_VERWACHTING_TOESTAND = [
  'GEPLAND',
  'BINNENKORT',
  'ACHTERSTALLIG',
  'ONDERHOUD_NU',
] as const
export type OnderhoudVerwachtingToestand =
  (typeof ONDERHOUD_VERWACHTING_TOESTAND)[number]

export const ONDERHOUD_VERWACHTING_TOESTAND_LABELS: Record<
  OnderhoudVerwachtingToestand,
  string
> = {
  GEPLAND: 'Gepland',
  BINNENKORT: 'Binnenkort',
  ACHTERSTALLIG: 'Achterstallig',
  ONDERHOUD_NU: 'Onderhoud nu',
}

/** Badge-variant per toestand (shadcn Badge-varianten, zelfde mapping als APK). */
export const ONDERHOUD_VERWACHTING_BADGE_VARIANT: Record<
  OnderhoudVerwachtingToestand,
  'success' | 'warning' | 'destructive'
> = {
  GEPLAND: 'success',
  BINNENKORT: 'warning',
  ACHTERSTALLIG: 'destructive',
  ONDERHOUD_NU: 'destructive',
}

/** Drempels waartegen "binnenkort" wordt geclassificeerd (wagenpark-instellingen). */
export interface OnderhoudVerwachtingDrempels {
  /** Melding bij onderhoud binnen X dagen (1-365, default 30). */
  drempelDagen: number
  /** Melding bij onderhoud binnen Y km (100-10.000, default 1.000). */
  drempelKm: number
}

/**
 * Classificeer een verwachting naar badge-toestand (FO-327 §2.4 + §6):
 *   - km < 0            -> ACHTERSTALLIG (al over tijd, ook als er dagen resteren)
 *   - km = 0            -> ONDERHOUD_NU ("Maintenance planned in 0 km")
 *   - dagen <= 0        -> ONDERHOUD_NU (rapport zegt: geen dagen meer over)
 *   - binnen drempels   -> BINNENKORT (dagen- óf km-grens; wat het eerst komt geldt)
 *   - anders            -> GEPLAND
 */
export function bepaalOnderhoudVerwachtingToestand(
  dagenTotOnderhoud: number | null,
  kmTotOnderhoud: number,
  drempels: OnderhoudVerwachtingDrempels,
): OnderhoudVerwachtingToestand {
  if (kmTotOnderhoud < 0) return 'ACHTERSTALLIG'
  if (kmTotOnderhoud === 0) return 'ONDERHOUD_NU'
  if (dagenTotOnderhoud != null && dagenTotOnderhoud <= 0) return 'ONDERHOUD_NU'
  if (
    (dagenTotOnderhoud != null && dagenTotOnderhoud <= drempels.drempelDagen) ||
    kmTotOnderhoud <= drempels.drempelKm
  ) {
    return 'BINNENKORT'
  }
  return 'GEPLAND'
}

const nlGetal = new Intl.NumberFormat('nl-NL')

/**
 * Weergave-tekst van de verwachting (FO-327 §2.4):
 *   - "Onderhoud binnen 18 dagen of 700 km" (of alleen km bij "-"-dagen)
 *   - "Achterstallig: 2.600 km over tijd (nog 68 dagen)"
 *   - "Onderhoud nu (0 km)"
 */
export function formatOnderhoudVerwachting(
  dagenTotOnderhoud: number | null,
  kmTotOnderhoud: number,
  toestand: OnderhoudVerwachtingToestand,
): string {
  if (toestand === 'ACHTERSTALLIG') {
    const basis = `Achterstallig: ${nlGetal.format(Math.abs(kmTotOnderhoud))} km over tijd`
    return dagenTotOnderhoud != null && dagenTotOnderhoud > 0
      ? `${basis} (nog ${nlGetal.format(dagenTotOnderhoud)} dagen)`
      : basis
  }
  if (toestand === 'ONDERHOUD_NU') {
    return `Onderhoud nu (${nlGetal.format(Math.max(kmTotOnderhoud, 0))} km)`
  }
  if (dagenTotOnderhoud != null) {
    return `Onderhoud binnen ${nlGetal.format(dagenTotOnderhoud)} dagen of ${nlGetal.format(kmTotOnderhoud)} km`
  }
  return `Onderhoud binnen ${nlGetal.format(kmTotOnderhoud)} km`
}

// ─── Wagenpark-instellingen (drempels + herkenningsprofiel) ──────────────────

/**
 * Valideert dat een door de beheerder ingevoerde onderwerp-regex compileert;
 * een kapotte regex mag de mail-poll nooit laten crashen.
 */
function isGeldigeRegex(patroon: string): boolean {
  try {
    new RegExp(patroon, 'i')
    return true
  } catch {
    return false
  }
}

/**
 * Gedeeld request-schema voor de wagenpark-onderhoud-instellingen (PUT).
 * De API gebruikt het én het backoffice-form valideert er client-side mee.
 */
export const wagenparkOnderhoudInstellingenSchema = z.object({
  /** Drempel "onderhoud binnen X dagen" (FO §5: 1-365, default 30). */
  drempelDagen: z.number().int().min(1).max(365),
  /** Drempel "onderhoud binnen Y km" (FO §5: 100-10.000, default 1.000). */
  drempelKm: z.number().int().min(100).max(10_000),
  /** Stilte-signaal: geen rapport ontvangen sinds N dagen (default 3). */
  stilteDagen: z.number().int().min(1).max(30),
  /**
   * Stilte-signaal aan/uit. Default UIT (2026-10-22): de leverancier-koppeling
   * leverde sinds 14-07 niets meer en het signaal werd een dagelijkse
   * melding + mail die niemand kon wegklikken. Wagenparkbeheer zet het weer
   * aan zodra er een actieve koppeling is.
   */
  stilteSignaalActief: z.boolean().default(false),
  /** Herkenningsprofiel: afzender-domein (bv. "telematica.nl"); leeg = geen afzender-eis. */
  profielAfzenderDomein: z.string().trim().max(200).nullable(),
  /** Herkenningsprofiel: onderwerp-regex (case-insensitive); leeg = geen onderwerp-eis. */
  profielOnderwerpRegex: z
    .string()
    .trim()
    .max(500)
    .nullable()
    .refine((v) => v == null || v === '' || isGeldigeRegex(v), {
      message: 'Geen geldige reguliere expressie',
    }),
  /** Herkenningsprofiel: bijlagenaam-patroon (glob, bv. "report_*.pdf"). */
  profielBijlagenaamPatroon: z.string().trim().min(1).max(200),
})
export type WagenparkOnderhoudInstellingen = z.infer<
  typeof wagenparkOnderhoudInstellingenSchema
>

/** Defaults conform FO-327 (§2.5 drempels, §11 profiel; bijlage sinds 2026-10 pdf óf xlsx). */
export const WAGENPARK_ONDERHOUD_INSTELLINGEN_DEFAULTS: WagenparkOnderhoudInstellingen =
  {
    drempelDagen: 30,
    drempelKm: 1_000,
    stilteDagen: 3,
    stilteSignaalActief: false,
    profielAfzenderDomein: null,
    // 2026-10: Mobilisights mailt "A report has just been generated (StreepOne)"
    // met `report_<datum>_235959.xlsx`; het oude PDF-onderwerp blijft herkend.
    // Het Mobilisights-onderwerp is generiek ("A report has just been
    // generated") - pas met de accountnaam `(StreepOne)` erbij is het ons
    // onderhoudsrapport. Geen afzender-domein-default: een domeinwissel bij de
    // leverancier zou de rapporten anders stil laten wegvallen; de bijlagenaam
    // `report_*` blijft het tweede herkenningssignaal.
    profielOnderwerpRegex:
      'vehicle\\s+maintenance\\s+report|a\\s+report\\s+has\\s+just\\s+been\\s+generated\\s+\\(streepone\\)',
    profielBijlagenaamPatroon: 'report_*',
  }

// ─── Koppel-actie (werkvoorraad -> voertuig) ─────────────────────────────────

/**
 * Request-schema van de koppel-actie: een werkvoorraad-regel ("rapportregel
 * zonder voertuig") aan een voertuig koppelen. Legt het VIN van de regel als
 * chassisnummer op het voertuig vast (zelflerend: volgende rapporten matchen
 * automatisch).
 */
export const koppelOnderhoudsrapportRegelSchema = z.object({
  voertuigId: z.string().min(1),
})
export type KoppelOnderhoudsrapportRegelInput = z.infer<
  typeof koppelOnderhoudsrapportRegelSchema
>

// ─── Enum-spiegels (Prisma) ──────────────────────────────────────────────────

// Soort rapport: het onderhoudsrapport (verwachtingen + signalen + km) of het
// wekelijkse km-rapport van de tracker ("Rapport kilometerteller", alleen km).
export const ONDERHOUD_RAPPORT_SOORT = ['ONDERHOUD', 'KM_STAND'] as const
export type OnderhoudRapportSoort = (typeof ONDERHOUD_RAPPORT_SOORT)[number]

export const ONDERHOUD_RAPPORT_SOORT_LABELS: Record<OnderhoudRapportSoort, string> = {
  ONDERHOUD: 'Onderhoudsrapport',
  KM_STAND: 'Km-rapport',
}

export const ONDERHOUD_RAPPORT_INGEST_STATUS = [
  'NIEUW',
  'VERWERKT',
  'DUPLICAAT',
  'GEEN_BIJLAGE',
  'FOUT',
  // Fase F (spec 44) — rapport-PDF door de malware-scan geblokkeerd; alleen
  // een ADMIN kan vrijgeven via de quarantaine-tab in de mail-module.
  'QUARANTAINE',
] as const
export type OnderhoudRapportIngestStatus =
  (typeof ONDERHOUD_RAPPORT_INGEST_STATUS)[number]

export const ONDERHOUD_RAPPORT_INGEST_STATUS_LABELS: Record<
  OnderhoudRapportIngestStatus,
  string
> = {
  NIEUW: 'Nieuw',
  VERWERKT: 'Verwerkt',
  DUPLICAAT: 'Duplicaat',
  GEEN_BIJLAGE: 'Geen bijlage',
  FOUT: 'Fout',
  QUARANTAINE: 'Quarantaine (malware-scan)',
}

export const ONDERHOUD_RAPPORT_REGEL_RESULTAAT = [
  'GEMATCHT',
  'WERKVOORRAAD',
  'GEKOPPELD',
  'GENEGEERD_VERKOCHT',
  'ONGELDIG',
] as const
export type OnderhoudRapportRegelResultaat =
  (typeof ONDERHOUD_RAPPORT_REGEL_RESULTAAT)[number]

export const ONDERHOUD_RAPPORT_REGEL_RESULTAAT_LABELS: Record<
  OnderhoudRapportRegelResultaat,
  string
> = {
  GEMATCHT: 'Gematcht',
  WERKVOORRAAD: 'Werkvoorraad',
  GEKOPPELD: 'Gekoppeld',
  GENEGEERD_VERKOCHT: 'Genegeerd (verkocht/afgevoerd)',
  ONGELDIG: 'Ongeldige rij',
}

// ─── DTO's ───────────────────────────────────────────────────────────────────

/**
 * Samenvatting van de actuele onderhoud-verwachting van één voertuig
 * (nieuwste rapport wint) — embedded in lijst-, detail- en Mijn voertuig-DTO's.
 */
export interface OnderhoudVerwachtingSamenvattingDTO {
  /** ISO datetime van de generatiedatum van het bronrapport ("laatst gemeld op"). */
  rapportDatum: string
  odometer: number
  dagenTotOnderhoud: number | null
  kmTotOnderhoud: number
  omschrijving: string | null
  toestand: OnderhoudVerwachtingToestand
  /** Weergave-tekst, bv. "Onderhoud binnen 18 dagen of 700 km". */
  tekst: string
  /**
   * True wanneer dit voertuig niet meer in het nieuwste vlootrapport voorkomt
   * (de verwachting is verouderd; UI toont "laatst gemeld op ...").
   */
  verdwenenUitRapport: boolean
}

/** Eén datapunt uit de historie-reeks op het onderhoud-tabblad. */
export interface VoertuigOnderhoudVerwachtingDTO {
  id: string
  voertuigId: string
  rapportDatum: string
  odometer: number
  dagenTotOnderhoud: number | null
  kmTotOnderhoud: number
  omschrijving: string | null
}

/** Eén rapportregel in de ingest-log / werkvoorraad. */
export interface OnderhoudRapportRegelDTO {
  id: string
  ingestId: string
  rijNummer: number
  vin: string
  odometer: number | null
  dagenTotOnderhoud: number | null
  kmTotOnderhoud: number | null
  omschrijving: string | null
  resultaat: OnderhoudRapportRegelResultaat
  voertuigId: string | null
  /** Kenteken van het gematchte/gekoppelde voertuig (leesbaar in lijsten). */
  kenteken: string | null
  waarschuwing: string | null
  gekoppeldOp: string | null
  gekoppeldDoorNaam: string | null
  /** ISO datetime van de generatiedatum van het bronrapport. */
  rapportDatum: string | null
  createdAt: string
}

/** Eén ingelezen rapport-mail in de ingest-log. */
export interface OnderhoudRapportIngestDTO {
  id: string
  mailbox: string
  afzender: string | null
  onderwerp: string | null
  ontvangenOp: string | null
  soort: OnderhoudRapportSoort
  status: OnderhoudRapportIngestStatus
  bestandsnaam: string | null
  rapportDatum: string | null
  parseMethode: 'DETERMINISTISCH' | 'AI' | null
  aantalRijen: number
  aantalGematcht: number
  aantalWerkvoorraad: number
  aantalGenegeerd: number
  foutmelding: string | null
  verwerktOp: string | null
  createdAt: string
}

/** Voertuig zonder chassisnummer in de vulactie-werkvoorraad "VIN ontbreekt". */
export interface VoertuigZonderVinDTO {
  id: string
  kenteken: string
  merk: string
  model: string
  status: string
}
